import { CheckCircle2, Factory, Loader2, Plus } from "lucide-react";
import { useState } from "react";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DatePicker } from "@/components/shared/date-picker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";
import { formatCurrencyRounded } from "@/lib/utils";

import { ProductionMenuCard } from "./production-menu-card";
import { useProductionForm } from "./use-production-form";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ProductionDialog({ open, onOpenChange, onSuccess }: Props) {
  const { preview: sequencePreview } = useNextNumberPreview("stock_production");
  const form = useProductionForm({ open, onOpenChange, onSuccess });
  const { isSubmitting } = form;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const cannotSubmit = !form.isReady || isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <Factory className="h-5 w-5" />
            <DialogTitle>Mulai Batch Produksi Baru</DialogTitle>
          </div>
          <DialogDescription>
            Siapkan stok barang jadi untuk rider. Satu batch bisa berisi
            beberapa menu, masing-masing dari bahan sesuai resepnya.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Top Info Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">
                No. Dokumen (Otomatis)
              </Label>
              <div className="mt-1 flex h-9 items-center rounded-md border bg-muted/40 px-3 font-mono text-sm font-semibold">
                {sequencePreview || "PRD/..."}
              </div>
            </div>

            <div>
              <Label htmlFor="prod-date">Tanggal Produksi</Label>
              <DatePicker
                value={form.date}
                onChange={form.setDate}
                className="mt-1"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {form.menus.map((menu, index) => (
            <ProductionMenuCard
              key={menu.key}
              menu={menu}
              index={index}
              form={form}
            />
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={form.addMenu}
            disabled={isSubmitting}
            className="w-full min-h-[40px] sm:min-h-9"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Tambah Menu
          </Button>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="prod-note">Catatan Produksi (Opsional)</Label>
            <Textarea
              id="prod-note"
              placeholder="Contoh: Batch pagi untuk persiapan event car free day..."
              value={form.generalNote}
              onChange={(e) => form.setGeneralNote(e.target.value)}
              rows={2}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <DialogFooter className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="min-h-[40px] sm:min-h-9"
          >
            Tutup
          </Button>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => form.submit(false)}
              disabled={cannotSubmit}
              className="min-h-[40px] sm:min-h-9"
            >
              Simpan sebagai Draft
            </Button>
            <Button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={cannotSubmit}
              className="min-h-[40px] sm:min-h-9"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Memproses...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Simpan & Selesaikan
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Selesaikan batch produksi?"
        description="Stok langsung berubah dan tercatat di Kartu Stok."
        confirmLabel="Ya, Selesaikan"
        variant="default"
        isConfirming={isSubmitting}
        onConfirm={async () => {
          await form.submit(true);
          setConfirmOpen(false);
        }}
      >
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
          <li>
            Bahan baku dipotong dari stok bar (total{" "}
            {formatCurrencyRounded(form.totalInputCost)}).
          </li>
          {form.menus.map((m) => (
            <li key={m.key}>
              {form.menuSummary(m).productName ?? "Produk jadi"} sebanyak{" "}
              {m.targetYieldStr} masuk ke stok gudang.
            </li>
          ))}
        </ul>
      </ConfirmDialog>
    </Dialog>
  );
}
